import { ReactElement } from "react"
import { useFormContext } from "react-hook-form"

import InputNotFound from "./inputNotFound"
import { Datasets } from "../../../../constants/enums"
import {
    CHECKBOX_INPUT,
    DATE_INPUT,
    G100_DEVICE,
    METER_NUMBER_KW,
    PHASE_CODE_KW,
    RADIO_INPUT,
    SELECT_INPUT,
    SELECT_OPTIONS_KW,
    SUPPLEMENTING_INFO_KW,
    TEXT_INPUT,
} from "../../../../constants/generalConstants"
import { TT_AMENDMENT_MODE_DISABLED } from "../../../../constants/tooltips"
import {
    IBasicInputSpec,
    isInputSpecNestedDiscrimUnion01,
    isPatternDefined,
} from "../../../../services/middleware/types/singleApplication/getSchemaTypes"
import { objectOnCondition } from "../../../../utils/general/generalUtils"
import ValidatedCheckBox from "../../../inputs/validated/validatedCheckBox"
import ValidatedDatePicker from "../../../inputs/validated/validatedDatePicker"
import ValidatedRadioGroup from "../../../inputs/validated/validatedRadioGroup"
import ValidatedAutocomplete from "../../../inputs/validated/validatedSelect"
import ValidatedTextField from "../../../inputs/validated/validatedTextField"
import AddressForm, { POSTCODE_KW } from "../recognisedInputs/addressForm"
import MpanRecognisedInput from "../recognisedInputs/mpanRecognisedInput"
import NestedDiscriminatedUnionInput from "../recognisedInputs/nestedDiscriminatedUnionInput"
import {
    ADDRESS_INPUT,
    DERIVED_INPUT,
    FIXED_INPUT,
    MPAN_RECOGNISED_INPUT,
    G100_SEARCH_RECOGNISED_INPUT_01,
    SINGLE_ATTACHMENT_INPUT_01,
    USER_ACCOUNT_DERIVED_INPUT,
    SEARCH_DEVICE_RECOGNISED_INPUT,
} from "../recognisedInputs/recognisedInputKeys"
import SelectNewDevice from "../recognisedInputs/selectNewDevice"
import SingleAttachmentInput from "../recognisedInputs/singleAttachmentInput"
import { SupplyPhases } from "../types/formGeneratorTypes"

export const FILE_URL_KEY = "fileUrl"

interface IInputGeneratorProps<T> {
    inputType: string
    registerName: string
    inputSpec: IBasicInputSpec
    watch: (key: string) => T
    programmaticallySetForm: (key: string, value: T) => void
    isAmendment: boolean
}

/**
 * Generates an input based on the input type. If the input type is not found
 * in the input type map, an error message is displayed.
 *
 * Not every input has been given a disable during amendment property. This was
 * done during release 1.4.0, and enough has been done to meet the scope of
 * 1.4.0 requirements. In future, we may wish to extend this to other inputs.
 */
const InputGenerator = <T,>({
    inputType,
    registerName,
    inputSpec,
    watch,
    programmaticallySetForm,
    isAmendment,
}: IInputGeneratorProps<T>): ReactElement | null => {
    const { displayIf } = inputSpec.json_schema_extra
    const displayIfValue = watch(displayIf?.property || "")
    const { control, setError, setValue } = useFormContext()
    if (displayIf) {
        const displayIfTarget = displayIf.value
        if (displayIfValue !== displayIfTarget) {
            return null
        }
    }

    // Recognised inputs with non BasicInputSpec type
    if (isInputSpecNestedDiscrimUnion01(inputSpec)) {
        return (
            <NestedDiscriminatedUnionInput<T>
                registerNameBase={registerName}
                discriminatorKey={inputSpec.discriminator.propertyName}
                control={control}
                nestedFormSpec={inputSpec}
                watch={watch}
                setError={setError}
                programmaticallySetForm={programmaticallySetForm}
            />
        )
    }

    const disabled =
        isAmendment && inputSpec.json_schema_extra.disableDuringAmendment

    const generateTooltip = (): string | undefined => {
        const { tooltip } = inputSpec.json_schema_extra
        if (!tooltip) return undefined
        return `${disabled ? TT_AMENDMENT_MODE_DISABLED : ""}${tooltip}`
    }

    // BasicInputSpec recognised inputs
    switch (inputType) {
        case TEXT_INPUT:
            return (
                <ValidatedTextField
                    control={control}
                    required={inputSpec.json_schema_extra.required}
                    registerName={registerName}
                    label={inputSpec.json_schema_extra.title}
                    tooltip={generateTooltip()}
                    disabled={disabled}
                    customRules={{
                        ...objectOnCondition(
                            isPatternDefined(inputSpec.json_schema_extra),
                            {
                                pattern: {
                                    value: new RegExp(
                                        inputSpec.json_schema_extra.pattern
                                            ?.value ?? ""
                                    ),
                                    message:
                                        inputSpec.json_schema_extra.pattern
                                            ?.message,
                                },
                            }
                        ),
                    }}
                    placeholder={inputSpec.json_schema_extra.placeholderText}
                />
            )
        case CHECKBOX_INPUT:
            return (
                <ValidatedCheckBox
                    control={control}
                    required={inputSpec.json_schema_extra.required}
                    registerName={registerName}
                    label={inputSpec.json_schema_extra.title}
                    checkboxOptions={inputSpec.json_schema_extra.options ?? {}}
                    disabled={disabled}
                />
            )
        case RADIO_INPUT:
            return (
                <ValidatedRadioGroup
                    control={control}
                    required={inputSpec.json_schema_extra.required}
                    registerName={registerName}
                    label={inputSpec.json_schema_extra.title}
                    radioOptions={inputSpec.json_schema_extra.options ?? {}}
                    tooltip={generateTooltip()}
                    disabled={disabled}
                />
            )
        case SELECT_INPUT:
            return (
                <ValidatedAutocomplete
                    control={control}
                    required={inputSpec.json_schema_extra.required}
                    registerName={registerName}
                    label={inputSpec.json_schema_extra.title}
                    tooltip={generateTooltip()}
                    options={
                        inputSpec.json_schema_extra.options?.[
                            SELECT_OPTIONS_KW
                        ] ?? []
                    }
                    disabled={disabled}
                />
            )
        case DATE_INPUT:
            return (
                <ValidatedDatePicker
                    control={control}
                    required={inputSpec.json_schema_extra.required}
                    registerName={registerName}
                    label={inputSpec.json_schema_extra.title}
                    setError={setError}
                    tooltip={generateTooltip()}
                    disabled={disabled}
                />
            )
        case ADDRESS_INPUT:
            return (
                <AddressForm
                    control={control}
                    disabled={disabled}
                    setValue={programmaticallySetForm}
                    isDefaults={!!watch(POSTCODE_KW)}
                    label="Search for Customer's Address Line 1" // TODO - needs to come from API
                />
            )
        case MPAN_RECOGNISED_INPUT:
            return (
                <MpanRecognisedInput
                    registerName={registerName}
                    control={control}
                    postCode={watch(POSTCODE_KW) as string}
                    isMpanEntered={!!watch(registerName)}
                    isMeterNumberEntered={!!watch(METER_NUMBER_KW)}
                    disabled={disabled}
                />
            )
        case SINGLE_ATTACHMENT_INPUT_01:
            return (
                <SingleAttachmentInput
                    title={inputSpec.json_schema_extra.title}
                    registerName={registerName}
                    fileUrlKey={FILE_URL_KEY}
                    initialUploads={[]}
                    programmaticallySetForm={programmaticallySetForm}
                />
            )
        case G100_SEARCH_RECOGNISED_INPUT_01:
            return (
                <SelectNewDevice
                    supplementingInfoKey={SUPPLEMENTING_INFO_KW}
                    registerName={registerName}
                    control={control}
                    watch={(k) => watch(k)}
                    deviceType={G100_DEVICE}
                    dataset={Datasets.TTR}
                    programmaticallySetForm={(k, v) => setValue(k, v)}
                    setClass={() => ({})}
                    phases={watch(PHASE_CODE_KW) as SupplyPhases}
                />
            )
        case SEARCH_DEVICE_RECOGNISED_INPUT:
            return null
        case DERIVED_INPUT:
            return null
        case FIXED_INPUT:
            return null
        case USER_ACCOUNT_DERIVED_INPUT:
            return null
        default:
            return <InputNotFound inputName={inputType} />
    }
}

export default InputGenerator
